import React, { useEffect, useState, useRef, Fragment, useCallback, useMemo } from 'react';
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import moment from 'moment';

// Helpers
import { GetFormatForMoment, completeColumns, checkDeviceData, validateJsonObject } from 'Core/data/Helpers';

// Components
import { Grid, Icon } from '@mui/material';
import { Timezone } from 'Components/uiControls';

// Custom Components
import Preferences from "Core/containers/Preferences";
import {
    TableView,
    useTablePagination,
    IconButton,
    TableButtons,
    DynamicTooltip,
    Pill,
    SearchInput,
    MenuComponent,
    Tooltip,
    Icons
} from '@gpstrackit/gtc-react-controls';
import { NotificationManager } from "../../../../components/Notifications";

//Views
import DriverInfo from './modals/driverInfo';
import AssetInfo from './modals/assetInfo';
import EventInfo from './modals/eventInfo';
import AssignStatusModal from './modals/assingStatus';
import CaseInfo from '../events/modals/caseInfo';
import VideoAssignDriver from "Modules/.common/components/VideoAssignDriver";

// Redux Actions
import { reset, driverPerformanceSearchEvents, bulkCreateDriverPerformanceOnEventCasesFull } from './redux/slice';
import { openModalEditEvent } from './modals/editEvent/redux/slice';
import { sendGenericExport, savePreference, clearRedux } from 'Redux/actions';
import { openModalAdvancedFilters } from './modals/advancedFilters/redux/slice';
import { openModalAssignStatus } from './modals/assingStatus/redux/slice';
import { openVideoAssignDriverModal } from "Modules/.common/components/VideoAssignDriver/redux/slice";


// Helpers
import { getInitialRange, getEventIcon } from '../../.globals/helpers';

// Constants
const KEY_PREFERENCE = "DrpEventsTab";

//Modals
import AdvancedFilters from './modals/advancedFilters';
import EditEvent from './modals/editEvent';

// Styles
import './index.scss';

// Initial State
const INITIAL_STATE = {
    rangeDate: {
        start: moment.utc().subtract(7, 'days').startOf('day').toDate(),
        end: moment.utc().subtract(1, 'days').endOf('day').toDate()
    },
    timeFrame: 'last7days',
    tags: [],
    events: [],
    drivers: [],
    status: [],
    driverDisputed: false,
    openSearch: ""
}

// Helper function to parse URL parameters and convert them to filter format
// Uses prefixed parameters (eventsRange, eventsStartDate, etc.) to avoid conflicts with other tabs
const parseUrlToFilters = (urlParams, drivers, tags, riskEventsByDriver, riskEventsByTag, messages) => {
    const filters = { ...INITIAL_STATE };

    // Parse range parameter with 'events' prefix
    if (urlParams.get('eventsRange')) {
        filters.timeFrame = urlParams.get('eventsRange');
        let rangeDate = getInitialRange(filters.timeFrame)
        if (rangeDate) {
            filters.rangeDate = {
                start: rangeDate.start,
                end: rangeDate.end
            };
        }
    }

    // Parse custom date range with 'events' prefix
    if (urlParams.get('eventsStartDate') && urlParams.get('eventsEndDate')) {
        filters.timeFrame = 'custom';
        let rangeDate = getInitialRange('custom', urlParams.get('eventsStartDate'), urlParams.get('eventsEndDate'), true)
        filters.rangeDate = {
            start: rangeDate.start,
            end: rangeDate.end
        };
    }

    // Parse event names filter with 'events' prefix
    if (urlParams.get('eventsNames')) {
        filters.events = urlParams.get('eventsNames').split(',').filter(Boolean).map(event => ({
            key: event,
            label: messages[`driver_performance_${event.replace(/\s+/g, '')}`] || event,
            eventName: event,
            value: event,
            events: [event]
        }));
    }

    // Parse driver filter with 'events' prefix
    if (urlParams.get('eventsDriverId')) {
        const driverIdsString = urlParams.get('eventsDriverId');
        let catalogDrivers = []
        if (drivers?.length) {
            catalogDrivers = drivers
        } else {
            catalogDrivers = riskEventsByDriver?.data?.items || []
            catalogDrivers = catalogDrivers.map((driver) => ({ id: driver?.driverId, name: driver?.driverName }))
        }
        let currentDriver = catalogDrivers?.find(driver =>
            String(driver?.driverId) === String(driverIdsString) ||
            String(driver?.id) === String(driverIdsString)
        );

        if (currentDriver) {
            filters.drivers = [{ ...currentDriver }]
        } else if (!currentDriver && driverIdsString) {
            filters.drivers = [{ id: driverIdsString, name: '' }]
        }
    }

    // Parse tag filter with 'events' prefix
    if (urlParams.get('eventsTagId')) {
        const tagIdsString = urlParams.get('eventsTagId');
        let catalogTags = []
        if (tags.length) {
            catalogTags = tags
        } else {
            catalogTags = riskEventsByTag?.data?.items || []
            catalogTags = catalogTags.map((tag) => ({ id: tag?.tagId, label: tag?.tagName }))
        }

        let currentTag = catalogTags?.find(tag =>
            String(tag.id) === String(tagIdsString)
        );
        if (currentTag) {
            filters.tags = [{ ...currentTag }]
        }
    }

    return filters;
};

/**
 * Main component for displaying driver performance events
 * @returns {JSX.Element} - Rendered component
 */
export const DriverPerformanceEvents = (props) => {
    const { messages, locale } = useIntl();
    const dispatch = useDispatch();
    const location = useLocation();
    const limitElementsSelected = 100;

    // Refs
    const eventsTableRef = useRef();

    // Local state
    const [loadPreference, setLoadPreferences] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [formData, setFormData] = useState(INITIAL_STATE);
    const [preferences, setPreferences] = useState({});
    const [loadColumns, setLoadColumns] = useState(false);
    const [page, setPage] = useState(1);
    const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);
    const riskEventsByDriver = useSelector(state => state?.dashboardRedux?.gadgetsData?.RiskEventsByDriver || {});
    const riskEventsByTag = useSelector(state => state?.dashboardRedux?.gadgetsData?.RiskEventsByTag || {});

    // Redux state
    const { loading, error, events, nextToken } = useSelector((state) => state?.driverPerformanceEventsTabRedux || {});
    const openModalFilters = useSelector((state) => state?.driverPerformanceEventsTabAdvancedFiltersRedux?.open);
    const openModalEdit = useSelector((state) => state?.driverPerformanceEventsTabEditEventRedux?.open);
    const openModalAssignStatusVar = useSelector((state) => state?.driverPerformanceEventsTabAssignStatusSliceRedux?.open);
    const openModalVideoAssignDriverVar = useSelector(state => state.videoAssignDriverRedux.open);

    const driverDetailDateRange = useMemo(() => {
        const conditions = buildConditions(formData || {});
        const dateRangeCondition = conditions?.find((item) => item?.range)?.range;

        if (!dateRangeCondition?.GTE || !dateRangeCondition?.LTE) return null;

        return {
            startDate: new Date(dateRangeCondition.GTE).toISOString(),
            endDate: new Date(dateRangeCondition.LTE).toISOString(),
        };
    }, [formData]);
    const loadBulkCreateEventCases = useSelector(state => state.driverPerformanceEventsTabRedux?.loadBulkCreateEventCases);
    const exportResult = useSelector((state) => state?.commomRedux?.exportResult);
    const exportName = useSelector((state) => state?.commomRedux?.exportName);
    const authenticatedId = useSelector(state => state?.securityRedux?.authenticatedId);
    const drivers = useSelector((state) => state?.catalogsRedux?.drivers);
    const tags = useSelector(state => state?.catalogsRedux?.tags);
    const user = useSelector(state => state?.securityRedux?.user || {});
    const { dateformat, timeZone } = user;
    const format = GetFormatForMoment(dateformat);

    // drivers catalog is loaded via useEffect
    const paginationEvents = useTablePagination({
        dispatch,
        action: driverPerformanceSearchEvents,
        searchFields: ["openSearch"],
        tableRef: eventsTableRef,
        sendGenericExport,
        exportName: "eventsTab",
    });

    useEffect(() => {
        if (loadBulkCreateEventCases) {
            NotificationManager.success(
                messages['driver_performance_drivers_petition_coach_now'],
                messages["success"],
                3000,
                null,
                null,
                "success"
            )
        }
    }, [loadBulkCreateEventCases]);


    const applyChanges = (field, value) => {
        if (typeof field === 'object' && field !== null) {
            const newFilters = field;
            let updatedParams = {
                _conditions: buildConditions({ ...newFilters, page: 1 }),
            };
            // Reset the table page
            setPage(1);
            eventsTableRef?.current?.setPage(1);
            paginationEvents.refresh(updatedParams);
            return;
        }

        let updatedParams = {
            _conditions: buildConditions({ ...formData, page: 1 }),
        };

        if (field === 'openSearch') {

            updatedParams = {
                _conditions: buildConditions({ ...formData, openSearch: value, page: 1 }),
            };
            setFormData(prev => ({ ...prev, openSearch: value }));
            if (window.typingTime) {
                clearTimeout(window.typingTime);
            }
            window.typingTime = setTimeout(() => {
                savePreferences([
                    {
                        key: "openSearch",
                        value: value
                    }
                ])
                paginationEvents.refresh(updatedParams);
            }, 1000);
        } else {
            paginationEvents.refresh(updatedParams);
        }
    }

    // hasFilters function
    const hasFilters = useCallback(() => {
        const excludedKeys = ["rangeDate", "page", "openSearch"];
        const defaultValues = {
            timeFrame: "last7days",
            tags: [],
            events: [],
            drivers: [],
            openSearch: "",
            status: [],
            driverDisputed: false
        };

        return Object.keys(formData).some(key => {
            if (excludedKeys.includes(key)) {
                return false;
            }

            const currentValue = formData[key];
            const defaultValue = defaultValues[key];

            // For arrays, compare length and content
            if (Array.isArray(currentValue) && Array.isArray(defaultValue)) {
                return currentValue.length !== defaultValue.length ||
                    currentValue.some((item, index) => item !== defaultValue[index]);
            }

            // For other types, strict comparison
            return currentValue !== defaultValue;
        });
    }, [formData]);



    const savePreferences = (preferencesArray) => {

        const urlSearchParams = new URLSearchParams(location.search);
        const urlParamsArray = Array.from(urlSearchParams.entries());
        const hasUrlParams = urlParamsArray.length > 0;

        // Check if the only URL param is 'tab=events'
        const onlyTabEventsParam = hasUrlParams &&
            urlParamsArray.length === 1 &&
            urlSearchParams.get('tab') === 'events';

        const isColumnChanging = preferencesArray.length ? preferencesArray?.some(pref => pref.key === 'columns') : false;

        if (!hasUrlParams || isColumnChanging || onlyTabEventsParam) {
            if (!Array.isArray(preferencesArray)) {
                console.error("Expected an array of preferences");
                return;
            }

            let updatedPreferences = { ...preferences } || {};

            preferencesArray.forEach(({ key, value }) => {
                updatedPreferences[key] = value;
            });

            setPreferences(updatedPreferences);

            dispatch(savePreference({
                entityName: "User",
                entityId: authenticatedId,
                preferenceKey: KEY_PREFERENCE,
                preferenceValue: updatedPreferences
            }));
        }
    };

    let currentColumns = [
        {
            key: 'event',
            title: messages?.["driver_performance_events_event"],
            show: true,
            width: 230,
            export: true,
            exportValue: (row) => {
                const eventName = row?.eventName || '';
                const eventKey = eventName.replace(/\s+/g, "");
                const translations = {
                    'Asleep': messages['driver_performance_Asleep'] || 'Asleep',
                    'CameraObstructed': messages['driver_performance_CameraObstructed'] || 'Camera Obstructed',
                    'CloseFollowing': messages['driver_performance_CloseFollowing'] || 'Close Following',
                    'CollisionWarning': messages['driver_performance_CollisionWarning'] || 'Collision Warning',
                    'CyclistWarning': messages['driver_performance_CyclistWarning'] || 'Cyclist Warning',
                    'Distracted': messages['driver_performance_Distracted'] || 'Distracted',
                    'Drowsiness': messages['driver_performance_Drowsiness'] || 'Drowsiness',
                    'HardAcceleration': messages['driver_performance_HardAcceleration'] || 'Hard Acceleration',
                    'HardBrake': messages['driver_performance_HardBrake'] || 'Hard Brake',
                    'HardTurn': messages['driver_performance_HardTurn'] || 'Hard Turn',
                    'IdleNoDriver': messages['driver_performance_IdleNoDriver'] || 'Idle No Driver',
                    'LaneDeparture': messages['driver_performance_LaneDeparture'] || 'Lane Departure',
                    'NoSeatbelt': messages['driver_performance_NoSeatbelt'] || 'No Seatbelt',
                    'ParkingHit': messages['driver_performance_ParkingHit'] || 'Parking Hit',
                    'PassengerInside': messages['driver_performance_PassengerInside'] || 'Passenger Inside',
                    'PassengerSeatbelt': messages['driver_performance_PassengerSeatbelt'] || 'Passenger Seatbelt',
                    'PedestrianDetection': messages['driver_performance_PedestrianDetection'] || 'Pedestrian Detection',
                    'PostedSpeed': messages['driver_performance_PostedSpeed'] || 'Posted Speed',
                    'PotentialIncident': messages['driver_performance_PotentialIncident'] || 'Potential Incident',
                    'RapidAcceleration': messages['driver_performance_RapidAcceleration'] || 'Rapid Acceleration',
                    'Smoking': messages['driver_performance_Smoking'] || 'Smoking',
                    'SpeedThreshold': messages['driver_performance_SpeedThreshold'] || 'Speed Threshold',
                    'StopSignViolation': messages['driver_performance_StopSignViolation'] || 'Stop Sign Violation',
                    'Tailgating': messages['driver_performance_Tailgating'] || 'Tailgating',
                    'UsingPhone': messages['driver_performance_UsingPhone'] || 'Using Phone',
                    'Yawn': messages['driver_performance_Yawn'] || 'Yawn',
                    'PotentialIncidentMedium': messages['driver_performance_PotentialIncidentMedium'] || 'Potential Incident',
                    'PotentialIncidentHigh': messages['driver_performance_PotentialIncidentHigh'] || 'Potential Incident',
                    'PotentialIncidentLow': messages['driver_performance_PotentialIncidentLow'] || 'Potential Incident',
                    'UnassignedDriver': messages['driver_performance_UnassignedDriver'] || 'Unassigned Driver',
                    'SpeedOverPosted': messages['driver_performance_SpeedOverPosted'] || 'Speed Over Posted',
                    'SpeedOver': messages['driver_performance_SpeedOver'] || 'Speed Over'
                };
                return translations[eventKey] || eventName;
            },
            functionString: `
                const translations = {
                    'Asleep': '${messages['driver_performance_Asleep'] || 'Asleep'}',
                    'CameraObstructed': '${messages['driver_performance_CameraObstructed'] || 'Camera Obstructed'}',
                    'CloseFollowing': '${messages['driver_performance_CloseFollowing'] || 'Close Following'}',
                    'CollisionWarning': '${messages['driver_performance_CollisionWarning'] || 'Collision Warning'}',
                    'CyclistWarning': '${messages['driver_performance_CyclistWarning'] || 'Cyclist Warning'}',
                    'Distracted': '${messages['driver_performance_Distracted'] || 'Distracted'}',
                    'Drowsiness': '${messages['driver_performance_Drowsiness'] || 'Drowsiness'}',
                    'HardAcceleration': '${messages['driver_performance_HardAcceleration'] || 'Hard Acceleration'}',
                    'HardBrake': '${messages['driver_performance_HardBrake'] || 'Hard Brake'}',
                    'HardTurn': '${messages['driver_performance_HardTurn'] || 'Hard Turn'}',
                    'IdleNoDriver': '${messages['driver_performance_IdleNoDriver'] || 'Idle No Driver'}',
                    'LaneDeparture': '${messages['driver_performance_LaneDeparture'] || 'Lane Departure'}',
                    'NoSeatbelt': '${messages['driver_performance_NoSeatbelt'] || 'No Seatbelt'}',
                    'ParkingHit': '${messages['driver_performance_ParkingHit'] || 'Parking Hit'}',
                    'PassengerInside': '${messages['driver_performance_PassengerInside'] || 'Passenger Inside'}',
                    'PassengerSeatbelt': '${messages['driver_performance_PassengerSeatbelt'] || 'Passenger Seatbelt'}',
                    'PedestrianDetection': '${messages['driver_performance_PedestrianDetection'] || 'Pedestrian Detection'}',
                    'PostedSpeed': '${messages['driver_performance_PostedSpeed'] || 'Posted Speed'}',
                    'PotentialIncident': '${messages['driver_performance_PotentialIncident'] || 'Potential Incident'}',
                    'RapidAcceleration': '${messages['driver_performance_RapidAcceleration'] || 'Rapid Acceleration'}',
                    'Smoking': '${messages['driver_performance_Smoking'] || 'Smoking'}',
                    'SpeedThreshold': '${messages['driver_performance_SpeedThreshold'] || 'Speed Threshold'}',
                    'StopSignViolation': '${messages['driver_performance_StopSignViolation'] || 'Stop Sign Violation'}',
                    'Tailgating': '${messages['driver_performance_Tailgating'] || 'Tailgating'}',
                    'UsingPhone': '${messages['driver_performance_UsingPhone'] || 'Using Phone'}',
                    'Yawn': '${messages['driver_performance_Yawn'] || 'Yawn'}',
                    'PotentialIncidentMedium': '${messages['driver_performance_PotentialIncidentMedium'] || 'Potential Incident'}',
                    'PotentialIncidentHigh': '${messages['driver_performance_PotentialIncidentHigh'] || 'Potential Incident'}',
                    'PotentialIncidentLow': '${messages['driver_performance_PotentialIncidentLow'] || 'Potential Incident'}',
                    'UnassignedDriver': '${messages['driver_performance_UnassignedDriver'] || 'Unassigned Driver'}',
                    'SpeedOverPosted': '${messages['driver_performance_SpeedOverPosted'] || 'Speed Over Posted'}',
                    'SpeedOver': '${messages['driver_performance_SpeedOver'] || 'Speed Over'}'
                };
                
                const eventName = row?.eventName || '';
                const eventKey = eventName.replace(/\\s+/g, "");
                return translations[eventKey] || eventName;
            `,
            render: (row) => {
                let showPlayIcon = true;
                const hasDriver = row?.driverId && row?.driverId !== 0;

                //double parse device Data
                const deviceData = validateJsonObject(validateJsonObject(row?.deviceData));

                const hasCaseNumber = deviceData?.caseNumber != null && deviceData?.caseNumber !== undefined && deviceData?.caseNumber !== '';

                if (deviceData) {
                    try {
                        if (row?.unitType?.includes("lightmetrics") && (!deviceData?.url && !deviceData?.thumbnailUrl)) {
                            showPlayIcon = false
                        } else if (row?.unitType?.includes("SmartWitness") && (!deviceData?.urls.length > 0)) {
                            showPlayIcon = false
                        } else if (row?.linkedDevice?.manufacturerName?.includes("VidFleet") && (!deviceData?.url && !deviceData?.thumbnailUrl)) {
                            showPlayIcon = false
                        }
                    } catch (e) {
                        showPlayIcon = false
                    }
                }

                return (
                    <Grid container style={{ width: 240, display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
                        <Grid item style={{ flex: '1 1 auto', minWidth: 0 }}>
                            {row?.eventName ? (
                                <DynamicTooltip
                                    key={row?.id}
                                    trigger={
                                        <span
                                            style={{
                                                fontFamily: 'Outfit, sans-serif',
                                                cursor: 'pointer',
                                                fontSize: 14,
                                                fontWeight: 400,
                                                borderBottom: '1px dashed #595151cc',
                                                display: 'block',
                                                width: '100%',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {messages[`driver_performance_${row.eventName.replace(/\s+/g, "")}`] || row.eventName}
                                        </span>
                                    }
                                    openOn="click"
                                    size="medium"
                                    position="top"
                                >
                                    {(isOpen) => (
                                        <EventInfo
                                            eventName={row.eventName}
                                            openTooltip={isOpen}
                                        />
                                    )}
                                </DynamicTooltip>
                            ) : (
                                <span>-</span>
                            )}
                        </Grid>

                        <Grid item style={{ flex: '0 0 auto', marginLeft: 'auto', display: 'flex', alignItems: 'center', columnGap: 8 }}>
                            {(showPlayIcon && (row?.eventCode?.includes("CameraEvent") || ['lightmetrics', 'switness'].includes(row?.unitType))) && (
                                <Icon
                                    style={{ cursor: 'pointer', color: '#2d9eeb' }}
                                    onClick={() => dispatch(openModalEditEvent({ event: row }))}
                                >
                                    play_circle_outline
                                </Icon>
                            )}
                            <MenuComponent
                                icon="more_vert_icon"
                                placement="right"
                                options={[
                                    { name: messages['driver_performance_events_coach_now'], disabled: hasCaseNumber || loadBulkCreateEventCases || !hasDriver },
                                    { name: messages['driver_performance_events_setStatus'], disabled: false },
                                    { name: messages['driver_performance_events_assignDriver'], disabled: hasDriver },
                                ]}
                                onClick={(type, value) => {
                                    if (type.name === messages["driver_performance_events_coach_now"]) {
                                        if (hasDriver) {
                                            const eventIds = []; // structure DriverEventsInput in
                                            eventIds.push({
                                                driverId: row?.driverId,
                                                events: [{
                                                    rowId: row?.rowId,
                                                    unitId: row?.esn
                                                }]
                                            });
                                            setTimeout(() => { //using setTimeout to allow menu to close before opening modal
                                                dispatch(bulkCreateDriverPerformanceOnEventCasesFull({ driverIds: [row?.driverId], eventIds: eventIds }));
                                            }, 500);
                                        }

                                    } else if (type.name === messages["driver_performance_events_assignDriver"]) {
                                        setTimeout(() => { //using setTimeout to allow menu to close before opening modal
                                            dispatch(openVideoAssignDriverModal({ ...row, unitId: row?.deviceIdGps }))
                                        }, 500);
                                    } else if (type.name === messages["driver_performance_events_setStatus"]) {
                                        setTimeout(() => { //using setTimeout to allow menu to close before opening modal
                                            dispatch(openModalAssignStatus({ event: row }))
                                        }, 500);
                                    }

                                }}
                                variant="primary"
                            />
                        </Grid>
                    </Grid>
                )
            }
        },
        {
            key: 'date',
            title: messages?.['driver_performance_events_date'],
            show: true,
            width: 200,
            export: true,
            exportValue: (row) => {
                function formatDate(date, tz, formatDate) {
                    let dateToformat = moment(date)
                    return moment(dateToformat).tz(tz).format(formatDate);
                }

                function getAbbvTimezone(timezone) {
                    let abbr = moment.tz(timezone).zoneAbbr();
                    if (abbr === '-05') {
                        return 'COT'
                    } else {
                        return abbr;
                    }
                }

                let timezoneAbbr = getAbbvTimezone(timeZone)

                return row?.unitTime ? formatDate(row?.unitTime, timeZone, format + " hh:mm a") + ", " + timezoneAbbr : ''
            },
            functionString: `
                function formatDate(date,tz,formatDate) {
                    let dateToformat = moment(date)
                    let formatted = moment(dateToformat).tz(tz).format(formatDate)
                    return formatted;
                }

                function getAbbvTimezone(timezone) {
                    let abbr = moment.tz(timezone).zoneAbbr();
                    switch (abbr) {
                        case '-05':
                            return 'COT';
                        default:
                            return abbr;
                    }
                }

                let timeZone = "${timeZone}"
                let format = "${format}" + " hh:mm a"
                let timezoneAbbr = getAbbvTimezone(timeZone)

                return row?.unitTime ? formatDate(row?.unitTime,timeZone,format) + ", " + timezoneAbbr : ''
            `,
            render: (row) => (
                <Timezone
                    date={row?.unitTime}
                    dateFormat={dateformat}
                    timeFormat="hh:mm a"
                    timezone={timeZone}
                />
            )
        },
        {
            key: 'driver',
            title: messages?.['driver_performance_events_driver'],
            show: true,
            width: 250,
            export: true,
            exportValue: (row) => {
                return row?.driverName || messages?.['driver_performance_noDriver'] || 'No Driver';
            },
            functionString: `
                const translations = {
                    'NoDriver': '${messages['driver_performance_noDriver'] || 'No Driver'}'
                };
                
                function splitTextIntoLines(text, maxLength) {
                        let result = "";
                        for (let i = 0; i < text.length; i += maxLength) {
                            result += text.slice(i, i + maxLength) + "\\n";
                        }
                        return result.trim();
                };
                return row?.driverName ? splitTextIntoLines(row?.driverName, 30) : translations['NoDriver'];
            `,
            render: (row) => {
                const driverKey = `driver-${row?.driverId}`;
                const hasDriver = row?.driverId && row?.driverId !== 0;

                return (
                    hasDriver ? (
                        // Show tooltip with driver info if driver is assigned
                        <DynamicTooltip
                            key={row?.id}
                            trigger={
                                <span style={{
                                    fontFamily: 'Outfit, sans-serif',
                                    cursor: 'pointer',
                                    fontSize: 14,
                                    fontWeight: 400,
                                    borderBottom: '1px dashed #595151cc',
                                    display: 'inline-block'
                                }}>
                                    {row?.driverName}
                                </span>
                            }
                            openOn="click"
                            size="medium"
                            position="top"
                        >
                            {(isOpen) => (
                                <DriverInfo
                                    driverKey={driverKey}
                                    openTooltip={isOpen}
                                    driverId={row?.driverId}
                                    driverDetailStartDate={driverDetailDateRange?.startDate}
                                    driverDetailEndDate={driverDetailDateRange?.endDate}
                                    event={row}
                                />
                            )}
                        </DynamicTooltip>
                    ) : (
                        // Show assign driver button if no driver is assigned
                        <Grid container alignItems="center" spacing={1}>
                            <Grid item>
                                <span style={{
                                    fontFamily: 'Outfit, sans-serif',
                                    fontSize: 14,
                                    fontWeight: 400,
                                    color: '#999'
                                }}>
                                    {messages?.['driver_performance_noDriver'] || 'No Driver'}
                                </span>
                            </Grid>
                            <Grid item>
                                <IconButton
                                    tooltip={messages?.['driver_performance_assignDriver']}
                                    placement="top"
                                    icon="person_add"
                                    size="small"
                                    onClick={() => {
                                        dispatch(openVideoAssignDriverModal({ ...row, unitId: row?.deviceIdGps }))
                                    }}
                                    style={{ color: '#005bf8' }}
                                />
                            </Grid>
                        </Grid>
                    )
                );
            }
        },
        {
            key: 'asset',
            title: messages?.['driver_performance_events_asset'],
            show: true,
            width: 150,
            export: true,
            exportValue: (row) => {
                return row?.assetName || '';
            },
            functionString: 'return row?.assetName',
            render: (row) => {
                const assetKey = `asset-${row?.assetId}`;
                return (
                    row?.assetName ? (
                        <DynamicTooltip
                            key={row?.id}
                            trigger={<span style={{ fontFamily: 'Outfit, sans-serif', cursor: 'pointer', fontSize: 14, fontWeight: 400, borderBottom: '1px dashed #595151cc', display: 'inline-block' }}>{row.assetName}</span>}
                            openOn="click"
                            size="medium"
                            position="top"
                        >
                            {(isOpen) => (
                                <AssetInfo
                                    assetKey={assetKey}
                                    openTooltip={isOpen}
                                    assetId={row?.assetId}
                                    assetName={row?.assetName}
                                />
                            )}
                        </DynamicTooltip>
                    ) : (
                        <span>-</span>
                    )
                )
            }
        },
        {
            key: 'status',
            title: messages?.['driver_performance_events_status'],
            show: true,
            width: 100,
            export: true,
            exportValue: (row) => {
                const translations = {
                    'verified': messages['driver_performance_verified'] || 'Verified',
                    'noissue': messages['driver_performance_noissue'] || 'No Issue',
                    'falsepositive': messages['driver_performance_falsepositive'] || 'False Positive',
                    'highlightcoaching': messages['driver_performance_highlightcoaching'] || 'Queue for Coaching'
                };

                const disputedText = messages['driver_performance_disputed'] || 'Disputed';

                let eventStatus = null;
                let isDisputed = false;

                if (row?.deviceData) {
                    try {
                        let deviceData = row?.deviceData ? checkDeviceData(row?.deviceData) : null
                        eventStatus = deviceData?.caseEventStatus;
                        if (typeof eventStatus === 'object' && eventStatus !== null) {
                            eventStatus = eventStatus.id || null;
                        }
                        isDisputed = deviceData?.caseEventDisputed || false;
                    } catch (e) {
                        eventStatus = null;
                        isDisputed = false;
                    }
                }

                let statusText = eventStatus ? (translations[eventStatus] || eventStatus) : '';

                // If disputed but no status, just show 'Disputed'
                if (isDisputed && !statusText) {
                    return disputedText;
                }

                // If disputed and has status, show both
                if (isDisputed && statusText) {
                    return statusText + ' - ' + disputedText;
                }

                // Otherwise just return status (or empty string)
                return statusText;
            },
            functionString: `

                const translations = {
                    'verified': '${messages['driver_performance_verified'] || 'Verified'}',
                    'noissue': '${messages['driver_performance_noissue'] || 'No Issue'}',
                    'falsepositive': '${messages['driver_performance_falsepositive'] || 'False Positive'}',
                    'highlightcoaching': '${messages['driver_performance_highlightcoaching'] || 'Queue for Coaching'}'
                };

                const disputedText = '${messages['driver_performance_disputed'] || 'Disputed'}';

                function isValidJson(str) {
                    if (typeof str !== 'string') return false;
                    try {
                        JSON.parse(str);
                        return true;
                    } catch (e) {
                        return false;
                    }
                }
                
                function checkDeviceData(paramDeviceData) {
                    let deviceData = paramDeviceData;
                    if (deviceData && typeof deviceData === 'string' && isValidJson(deviceData)) {
                        deviceData = JSON.parse(paramDeviceData);
                        // Double parse porque a veces viene doble parseado
                        if (typeof deviceData === "string" && isValidJson(deviceData)) {
                            deviceData = JSON.parse(deviceData);
                        }
                    }
                    return deviceData;
                }
                
                let deviceData = null;
                let eventStatus = null;
                let isDisputed = false;
                
                if (row?.deviceData) {
                    deviceData = checkDeviceData(row.deviceData);
                    if (deviceData && typeof deviceData === 'object') {
                        eventStatus = deviceData.caseEventStatus;
                        if (typeof eventStatus === 'object' && eventStatus !== null) {
                            eventStatus = eventStatus.id || null;
                        }
                        isDisputed = deviceData.caseEventDisputed || false;
                    }
                }
                
                let statusText = eventStatus ? (translations[eventStatus] || eventStatus) : '';
                
                // If disputed but no status, just show 'Disputed'
                if (isDisputed && !statusText) {
                    return disputedText;
                }
                
                // If disputed and has status, show both
                if (isDisputed && statusText) {
                    return statusText + ' - ' + disputedText;
                }
                
                // Otherwise just return status (or empty string)
                return statusText;
            `,
            render: (row) => {
                let eventStatus = null;
                let isDisputed = false;

                if (row?.deviceData) {
                    try {
                        let deviceData = row?.deviceData ? checkDeviceData(row?.deviceData) : null
                        // Get status
                        eventStatus = deviceData.caseEventStatus;
                        if (typeof eventStatus === 'object' && eventStatus !== null) {
                            eventStatus = eventStatus.id || null;
                        }
                        // Get disputed
                        isDisputed = deviceData.caseEventDisputed || false;

                    } catch (e) {
                        eventStatus = null;
                        isDisputed = false;
                    }
                }
                let iconStyle = { width: 32, height: 28 };
                const eventIcon = getEventIcon(eventStatus, isDisputed, messages, iconStyle);
                return <>{eventIcon ? <div style={{ width: 60 }}>{eventIcon}</div> : <div></div>} </>
            }
        },
        {
            key: 'refCase',
            title: messages?.['driver_performance_events_refCase'],
            show: true,
            width: 150,
            export: true,
            exportValue: (row) => {
                const deviceData = validateJsonObject(validateJsonObject(row?.deviceData));
                return deviceData?.caseNumber || '';
            },
            functionString: `
                function isValidJson(str) {
                        if (typeof str === 'string') {
                            try {
                                return JSON.parse(str);
                            } catch (e) {
                                return str;
                            }
                        } else if (typeof str === 'object') {
                            return str;
                        }
                    }
                let deviceData = null;
                if (row?.deviceData) {
                    //double parse to get object
                    deviceData = isValidJson(isValidJson(row.deviceData));
                }
                return deviceData?.caseNumber || '';
            `,
            render: (row) => {
                const deviceData = validateJsonObject(validateJsonObject(row?.deviceData));
                return (
                    <div style={{ width: 100 }}>
                        {deviceData?.caseNumber ? (
                            <DynamicTooltip
                                key={row?.id}
                                trigger={<span style={{ fontFamily: 'Outfit, sans-serif', cursor: 'pointer', fontSize: 14, fontWeight: 400, borderBottom: '1px dashed #595151cc', display: 'inline-block' }}>{deviceData?.caseNumber}</span>}
                                openOn="click"
                                size="medium"
                                position="top"
                            >
                                {(isOpen) => (
                                    <CaseInfo
                                        caseNumber={deviceData?.caseNumber}
                                        openTooltip={isOpen}
                                        driverDetailStartDate={driverDetailDateRange?.startDate}
                                        driverDetailEndDate={driverDetailDateRange?.endDate}
                                    />
                                )}
                            </DynamicTooltip>
                        ) : (
                            <span></span>
                        )}
                    </div>
                )
            }
        },
    ]

    useEffect(() => {
        paginationEvents.setColumns(currentColumns);
        setSelectedItems([]);
        eventsTableRef?.current?.setPage(1);
        eventsTableRef?.current?.setSelectedItems([]);
    }, []);

    useEffect(() => {
        if (error) {
            let currentError = error?.error;

            if (currentError.includes("events are not active")) {
                // Extract event names after the colon from the backend message
                // format: The following events are not active in the asset profile: Potential Incident, Hard Brake.
                const colonIndex = currentError.indexOf(':');
                const rawEvents = colonIndex !== -1 ? currentError.substring(colonIndex + 1).replace(/\.$/, '').trim() : '';
                const eventNames = rawEvents.split(',').map(e => e.trim()).filter(Boolean);

                // Translate each event name dynamically using messages[`driver_performance_${key}`]
                const translatedEvents = eventNames.map(name => {
                    const key = name.replace(/\s+/g, '');
                    return messages[`driver_performance_${key}`] || name;
                }).join(', ');
                // Build the localized message with the translated event names
                const messageTemplate = messages?.['driver_performance_events_not_active_in_asset_profile']
                currentError = messageTemplate.replace('{events}', translatedEvents);
                NotificationManager.warning(currentError || "An error occurred", messages?.["driver_performance_events_warning"] || "Warning", 3000);
            } else {
                NotificationManager.error(messages?.["errorMessage"] || "An error occurred", messages?.["error"] || "Error", 3000);
            }
            
            dispatch(reset({ error: null }));
        }
        if (exportResult && exportName === "eventsTab") {
            NotificationManager.info(messages?.["exportInProcessBody"], messages?.["exportInProcessTitle"] || "Export", 4000);
            dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
        }
    }, [error, exportResult, exportName, messages, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(reset());
        };
    }, [dispatch]);


    useEffect(() => {
        if (preferences?.columns?.length && !loadColumns) {
            const preferenceMap = new Map(preferences.columns.map(col => [col.key, col]));
            const reorderedColumns = preferences.columns
                .map(pref => {
                    const col = currentColumns.find(c => c.key === pref.key);
                    return col ? { ...col, show: pref.show } : null;
                })
                .filter(Boolean);
            const newColumns = currentColumns
                .filter(col => !preferenceMap.has(col.key))
                .map(col => ({ ...col, show: true }));

            const finalColumns = [...reorderedColumns, ...newColumns];

            paginationEvents.setColumns(finalColumns);
            setPreferences({ ...preferences });
            setLoadColumns(true);
        }
    }, [preferences?.columns, loadColumns])

    useEffect(() => {
        if (paginationEvents.columns.length > 0) {
            setPreferences({ ...preferences, columns: paginationEvents.columns })
            setLoadColumns(false);
        }
    }, [locale, timeZone, dateformat]);

    const generateExportStyles = () => {
        return {
            0: {
                cellWidth: 60,
                fontSize: 11,
                fontStyle: "bold",
            }
        };
    };

    // Bulk option: Send to Coaching
    // Validations:
    // 1. Check if there are events without drivers
    // 2. Check if there are events that already have a case assigned
    // 3. Group events by driverId
    // 4. Convert map to array
    // 5. Dispatch bulk create action
    const bulkOptionSendToCoaching = useCallback((selectedItems) => {

        // 1. First, check if there are events without drivers
        const eventsWithoutDriver = selectedItems.filter(item => !item?.driverId || item?.driverId === 0);

        if (eventsWithoutDriver.length > 0) {
            NotificationManager.warning(
                messages?.['driver_performance_events_withoutDriver'] ||
                `Cannot send to coaching: ${eventsWithoutDriver.length} event(s) do not have a driver assigned`,
                messages?.['warning'] || "Warning",
                4000
            );
            return;
        }

        // 2. Then, check if there are events that already have a case assigned
        const eventsWithCase = selectedItems.filter(item => {
            const deviceData = validateJsonObject(validateJsonObject(item?.deviceData));
            return deviceData?.caseNumber != null && deviceData?.caseNumber !== undefined && deviceData?.caseNumber !== '';
        });

        if (eventsWithCase.length > 0) {
            NotificationManager.warning(
                messages?.['driver_performance_events_withCase'].replace("{count}", eventsWithCase.length) ||
                `Cannot send to coaching: ${eventsWithCase.length} event(s) already have a case assigned`,
                messages?.['warning'] || "Warning",
                4000
            );
            return;
        }


        let driverIds = [...new Set(selectedItems.map(item => item?.driverId).filter(Boolean))];
        // 3. Create a map to group events by driverId
        const eventsMap = new Map();

        selectedItems.forEach(item => {
            if (item?.driverId && item?.rowId) {
                const driverId = item.driverId;

                // If driver doesn't exist in map, create entry
                if (!eventsMap.has(driverId)) {
                    eventsMap.set(driverId, {
                        driverId: driverId,
                        events: []
                    });
                }

                // Add event to driver's events array
                eventsMap.get(driverId).events.push({
                    rowId: item.rowId,
                    unitId: item.esn
                });
            }
        });
        // 4.Convert map to array
        const eventIds = Array.from(eventsMap.values()) || [];

        if (driverIds.length === 0) {
            NotificationManager.warning(messages?.['driver_performance_noDriversSelected'] || "No drivers selected", messages?.['warning'] || "Warning", 3000);
            return;
        }

        // 5. Dispatch bulk create action
        dispatch(bulkCreateDriverPerformanceOnEventCasesFull({ driverIds: driverIds, eventIds: eventIds }));

    }, [dispatch, messages]);

    return (
        <Fragment>
            <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                style={{ marginBottom: 8 }}
            >
                <Grid item xs={6} sm={2} md={2}>
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={1}
                    >
                        <Grid item style={{ display: "flex" }}>
                            <IconButton
                                tooltip={messages?.["filters"]}
                                placement="bottom"
                                icon="filter_list"
                                size="small"
                                disabled={true || loading}
                                onClick={() => {
                                    dispatch(openModalAdvancedFilters({ open: !openModalFilters }));
                                }}
                                className={hasFilters() ? "eventsTab-filters-available" : ""}
                            />
                            <TableButtons
                                {...paginationEvents}
                                messages={messages}
                                buttons={["export", "orderColumn"]}
                                onExport={(type) => {
                                    if (selectedItems?.length) {
                                        if (type === "Csv") {
                                            paginationEvents?.tableRef.current.exportCSV();
                                        } else if (type === "Pdf") {
                                            paginationEvents?.tableRef.current.exportPDF();
                                        }
                                    } else {
                                        let headers = paginationEvents.columns.filter(col => col.export && col.show)
                                            .map(col => ({
                                                header: col.title,
                                                column: col.key,
                                                functionString: col.functionString || `return row?.${col.key}`
                                            }));

                                        const conditions = buildConditions({ ...formData });

                                        let dateRange = conditions?.find((item) => item.range);
                                        let tags = conditions?.find((item) => item.tagIds);
                                        let events = conditions?.find((item) => item.events);
                                        let drivers = conditions?.find((item) => item.drivers);
                                        let openSearch = conditions?.find((item) => item.openSearch);
                                        let status = conditions.find((item) => item.status);
                                        let driverDisputed = conditions.find((item) => item.driverDisputed);

                                        let payload = {
                                            openSearch: openSearch?.openSearch ? openSearch.openSearch.trim() : "",
                                            dateRange: {
                                                start: dateRange?.range?.GTE,
                                                end: dateRange?.range?.LTE
                                            },
                                            driverIds: drivers?.drivers?.length ? drivers?.drivers : [],
                                            status: status?.status?.length ? status?.status : [],
                                            eventNames: events?.events?.length ? events?.events : [],
                                            tagIds: tags?.tagIds?.length ? tags?.tagIds : [],
                                            driverDisputed: driverDisputed?.driverDisputed || false,
                                            excludedNoIssueEvents: formData?.excludedNoIssueEvents || false,
                                            limit: 50,
                                            nextToken: ""
                                        }

                                        dispatch(
                                            sendGenericExport(
                                                "eventsTab",
                                                type,
                                                driverPerformanceSearchEvents.query,
                                                payload,
                                                headers,
                                                {
                                                    unit: 'px',
                                                    size: 'A4',
                                                    orientation: 'landscape',
                                                    fontSize: 10,
                                                },
                                                null,
                                                true,
                                                true,
                                                generateExportStyles()
                                            )
                                        );
                                    }
                                }}
                                onChangeColumns={(columns) => {
                                    savePreferences([{ key: "columns", value: columns }])
                                    paginationEvents.setColumns(columns)
                                }}
                                columns={paginationEvents.columns}
                            />
                            {
                                (selectedItems?.length > 0 && selectedItems?.length <= limitElementsSelected)
                                    ?
                                    (
                                        <MenuComponent
                                            icon="library_add_check"
                                            placement="right"
                                            //disabled={true || loading}
                                            options={[
                                                { name: messages['driver_performance_bulkoptions_sendToCoaching'], disabled: loadBulkCreateEventCases },
                                                { name: messages['driver_performance_bulkoptions_setStatus'], disabled: false },
                                            ]}
                                            onClick={(type, value) => {
                                                if (type.name === messages["driver_performance_bulkoptions_sendToCoaching"]) {
                                                    // perform bulk send to coaching action
                                                    setTimeout(() => { //using setTimeout to allow menu to close before opening modal
                                                        bulkOptionSendToCoaching(selectedItems);
                                                    }, 500);

                                                } else if (type.name === messages["driver_performance_bulkoptions_setStatus"]) {
                                                    // Create a map to group events by driverId
                                                    const eventsToUpdate = selectedItems.map(item => ({
                                                        unitId: item?.esn, // ESN is the unitId
                                                        esn: item?.esn,
                                                        rowId: item?.rowId,
                                                        deviceData: item?.deviceData
                                                    }));
                                                    setTimeout(() => { //using setTimeout to allow menu to close before opening modal
                                                        dispatch(openModalAssignStatus({ events: eventsToUpdate, multipleEvents: true }));
                                                    }, 500);
                                                }
                                            }}
                                            variant="primary"
                                        />
                                    )
                                    :
                                    selectedItems?.length > limitElementsSelected
                                    &&
                                    <IconButton
                                        tooltip={messages?.["driver_performance_bulkoptions_selected_limit"]}
                                        placement="right"
                                        icon="library_add_check"
                                        size="small"
                                        disabled={true}
                                    />

                            }
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={6} sm={4} md={4} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap' }}>
                    <Tooltip
                        placement="bottom"
                        title={messages['driver_performance_driver_timeInfo_message']}
                    >
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                            <Icons name="help" size="medium" />
                        </span>
                    </Tooltip>
                    <Pill
                        textField={"label"}
                        item={{ id: "date", label: `${messages[`driver_performance_events_${formData?.timeFrame}`] || formData?.timeFrame} (${formData?.rangeDate?.start ? moment(formData.rangeDate.start).format(`${GetFormatForMoment(user?.dateformat) || 'MM/DD/YYYY'} hh:mm a`) : ''} - ${formData?.rangeDate?.end ? moment(formData.rangeDate.end).format(`${GetFormatForMoment(user?.dateformat) || 'MM/DD/YYYY'} hh:mm a`) : ''})`, color: '#005bf8' }}
                        variant='filled'
                        onClick={() => {
                            dispatch(openModalAdvancedFilters({ open: !openModalFilters }));
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={4} className="mb-10 text-right">
                    <SearchInput
                        placeholder={messages?.["driver_performance_searchInput"]}
                        searchText={formData?.openSearch || ""}
                        onSearchText={(value) => applyChanges('openSearch', value)}
                    />
                </Grid>
            </Grid>
            <Preferences
                preferencesKey={KEY_PREFERENCE}
                onInitialPreferences={(data) => {
                    if (!loadPreference) {
                        // Check for URL parameters first
                        const urlSearchParams = new URLSearchParams(location.search);

                        // Only consider relevant URL parameters for Events tab (with 'events' prefix)
                        const relevantEventsParams = ['eventsRange', 'eventsStartDate', 'eventsEndDate', 'eventsNames', 'eventsDriverId', 'eventsTagId'];
                        const hasRelevantUrlParams = relevantEventsParams.some(param => urlSearchParams.has(param));

                        let updatedFormData;

                        if (hasRelevantUrlParams && !urlParamsProcessed) {
                            // Priority: URL parameters over user preferences
                            const filtersFromUrl = parseUrlToFilters(urlSearchParams, drivers, tags, riskEventsByDriver, riskEventsByTag, messages);
                            updatedFormData = { ...filtersFromUrl, page: 1, excludedNoIssueEvents: true };
                            setUrlParamsProcessed(true);
                        } else {
                            // Fallback: Use user preferences
                            updatedFormData = {
                                ...formData, ...['rangeDate', 'tags', 'events', 'drivers', 'status', 'driverDisputed'].reduce((acc, key) => {
                                    if (data?.[key]) acc[key] = data[key];
                                    return acc;
                                }, {}),
                                page: 1
                            };
                            if (data?.timeFrame) {
                                const timeFrame = getInitialRange(data.timeFrame, data?.rangeDate?.start, data?.rangeDate?.end);
                                if (timeFrame?.start && timeFrame?.end) {
                                    updatedFormData = {
                                        ...updatedFormData,
                                        rangeDate: timeFrame,
                                        timeFrame: data.timeFrame
                                    }
                                }
                            }
                        }
                        const currentConditions = buildConditions(updatedFormData);
                        paginationEvents.refresh({ _conditions: currentConditions });
                        setFormData(updatedFormData)
                        setPreferences(data)
                        setLoadPreferences(true)
                    }
                }}
            >
                <TableView
                    {...paginationEvents}
                    keyColumn="countId"
                    data={events}
                    loading={loading}
                    topheight={250}
                    exportOrientation="landscape"
                    onSelectedItems={(items) => setSelectedItems(items)}
                    onChangeColumns={(columns) => {
                        let currentColumns = paginationEvents?.columns || []
                        let finalsColumns = completeColumns(currentColumns, columns)
                        savePreferences([{ key: "columns", value: finalsColumns }])
                        paginationEvents.setColumns(finalsColumns)
                    }}

                    infiniteScroll={true}
                    onChangePage={() => {
                        if (events?.length) {
                            let newPage = page + 1
                            setPage(newPage);
                            const currentConditions = buildConditions({ ...formData, page: newPage });
                            paginationEvents.refresh({ _conditions: currentConditions });
                        }
                    }}
                    nextToken={nextToken}
                    noMoreItemsMessage={messages?.['driver_performance_noMoreItems']}
                />
            </Preferences>
            <AdvancedFilters
                tableRef={paginationEvents}
                formData={formData}
                setFormData={setFormData}
                initialState={INITIAL_STATE}
                applyChanges={applyChanges}
                hasFilters={hasFilters}
                savePreferences={savePreferences}
            />
            {openModalEdit && <EditEvent />}
            {openModalAssignStatusVar && <AssignStatusModal />}
            {openModalVideoAssignDriverVar &&
                <VideoAssignDriver
                    isDriverBehavior={true}
                    refresh={() => {
                        let updatedParams = {
                            _conditions: buildConditions({ ...formData, page: 1 }),
                        };
                        // Reset the table page
                        setPage(1);
                        eventsTableRef?.current?.setPage(1);
                        paginationEvents.refresh(updatedParams);
                    }}
                />
            }
        </Fragment>
    );
};


const buildConditions = (formData) => {
    const conditions = [];
    // Validate that rangeDate exists before using it
    if (formData?.rangeDate?.start && formData?.rangeDate?.end) {
        const startUTC = formData?.timeFrame === 'custom'
            ? moment.utc(moment(formData.rangeDate.start).format('YYYY-MM-DD')).startOf('day').toDate()
            : formData.rangeDate.start;
        const endUTC = formData?.timeFrame === 'custom'
            ? moment.utc(moment(formData.rangeDate.end).format('YYYY-MM-DD')).endOf('day').toDate()
            : formData.rangeDate.end;

        conditions.push({ range: { fields: ["scheduledDate"], GTE: startUTC, LTE: endUTC } });
    }

    let allEvents = [];

    if (formData?.events?.length) {
        // Extract all events from the events array of each item
        formData.events.forEach(eventItem => {
            if (eventItem?.events && Array.isArray(eventItem.events)) {
                allEvents = [...allEvents, ...eventItem.events];
            }
        });

        if (allEvents.length > 0) {
            conditions.push({ events: allEvents });
        }
    }

    let allStatus = [];
    if (formData?.status?.length) {
        allStatus = formData?.status.map(item => item?.entityId)
        conditions.push({ status: allStatus });
    }

    if (formData?.driverDisputed) {
        conditions.push({ driverDisputed: true });
    }

    let allDrivers = [];
    if (formData?.drivers?.length) {
        allDrivers = [...allDrivers, ...formData.drivers.map(driver => driver?.driverId || driver?.id).filter(Boolean)];
        if (allDrivers.length > 0) {
            conditions.push({ drivers: allDrivers });
        }
    }

    let allTags = [];
    if (formData?.tags?.length) {
        allTags = [...allTags, ...formData.tags.map(tag => tag?.id).filter(Boolean)];
        if (allTags.length > 0) {
            conditions.push({ tagIds: allTags });
        }
    }

    if (formData.page) {
        conditions.push({ page: formData.page });
    }

    if (formData.openSearch) {
        conditions.push({ openSearch: formData.openSearch });
    }

    if (formData?.excludedNoIssueEvents) {
        conditions.push({ excludedNoIssueEvents: true });
    }

    return conditions;
};



export default DriverPerformanceEvents;
